import 'ol/ol.css';
import Feature from 'ol/Feature';
import {LineString} from 'ol/geom';
import {
  Pointer as PointerInteraction,
} from 'ol/interaction';

import {pointToFootDist,renderFeature,renderSelectFeature} from '../../utils/mapUtils';
import store from '@/store';

import _find from 'lodash/find';

export default class Drag extends PointerInteraction {
  constructor(option) {
    super({
      handleDownEvent: handleDownEvent,
      handleDragEvent: handleDragEvent,
      handleMoveEvent: handleMoveEvent,
      handleUpEvent: handleUpEvent,
    });

    /**
     * @type {import("../src/ol/coordinate.js").Coordinate}
     * @private
     */
    this.coordinate_ = null;

    /**
     * @type {string|undefined}
     * @private
     */
    this.cursor_ = 'pointer';

    /**
     * @type {Feature}
     * @private
     */
    this.feature_ = null;

    /**
     * @type {string|undefined}
     * @private
     */
    this.previousCursor_ = undefined;
    /**
     * 图形的类型
     */
    this.geometry_type_=null;

    this.source_=option.source;

    // 线的首位坐标
    this.first_coor=null;
    this.isFirst=false;
    this.last_coor=null;
    this.isLast=false;
    // 初始化函数
    this.initDrag_=initDrag;
  }
}

/**
 * @param {import("../src/ol/MapBrowserEvent.js").default} evt Map browser event.
 * @return {boolean} `true` to start the drag sequence.
 */
function handleDownEvent(evt) {
  // 重新初始化拖动
  this.initDrag_();
 
  // 判断是否是整体移动, 如果有多个要素，说明是整体移动
  let tempF=store.state.simulation.curFeatures;
  if(tempF.length>1){
    this.coordinate_ = evt.coordinate;
    // 判断当前的鼠标状态，如果鼠标不在元素上，则说明是不移动
    const element = evt.map.getTargetElement();
    if(element.style.cursor!=this.cursor_){
      // 触发事件监听
      this.dispatchEvent({type:"dragstart",event:evt,feature:null});
      return false;
    }else{
      // 触发事件监听
      this.dispatchEvent({type:"dragstart",event:evt,feature:tempF});
      return true;
    }

  }else{
    /**
     * 单独移动
     */
    const map = evt.map;
    const hitTolerance=10; // 吸附半径
    const feature = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
      return feature;
    },{
      hitTolerance:hitTolerance
    });

    if (feature) {
      this.coordinate_ = evt.coordinate;
      this.feature_ = feature;

      let geometry_=feature.getGeometry();
      // 如果要拖动点元素为线段
      if(geometry_ instanceof LineString){
          this.geometry_type_="LineString";
          // 当点击了线段的首尾端点的时候，需要修改线段的首尾位置，而不是移动线段
          // 判断是否点击了第一个点,这个时候涉及到线段的方向问题
          this.first_coor=geometry_.getFirstCoordinate();
          let first_pixel=map.getPixelFromCoordinate(this.first_coor);
          if(Math.abs(evt.pixel[0]-first_pixel[0])<=hitTolerance&&Math.abs(evt.pixel[1]-first_pixel[1])<=hitTolerance){
              this.isFirst=true;
              this.isLast=false;
              this.geometry_type_="LineString_Point"; // 移动线段的端点
          }
          // 判断是否点击了最后一个点
          this.last_coor=geometry_.getLastCoordinate();
          let last_pixel=map.getPixelFromCoordinate(this.last_coor);
          if(Math.abs(evt.pixel[0]-last_pixel[0])<=hitTolerance&&Math.abs(evt.pixel[1]-last_pixel[1])<=hitTolerance){
              this.isLast=true;
              this.isFirst=false;
              this.geometry_type_="LineString_Point"; // 移动线段的端点
          }
      }
    }

    // 触发事件监听
    this.dispatchEvent({type:"dragstart",event:evt,feature:this.feature_});

    return !!feature;
  }
}

/**
 * @param {import("../src/ol/MapBrowserEvent.js").default} evt Map browser event.
 */
function handleDragEvent(evt) {
  // 进行整体移动
  // 判断是否是整体移动, 如果有多个要素，说明是整体移动
  let tempF=store.state.simulation.curFeatures;
  if(tempF.length>1){
    let deltaX = evt.coordinate[0] - this.coordinate_[0];
    let deltaY = evt.coordinate[1] - this.coordinate_[1];
    this.coordinate_[0] = evt.coordinate[0];
    this.coordinate_[1] = evt.coordinate[1];
    tempF.forEach(feat=>{
      let geometry = feat.getGeometry();
      // 进行管道和节点的关系割裂
      if(geometry instanceof LineString){ // 如果是管道
        /**
         * 判断关联节点是否是在移动范围内，如果不在，则删除
         */
        let node1=feat.get("node1")||"";
        let first_point=this.source_.getFeatureById(node1);
        if(first_point){
          let first_fid=first_point.getId();
          let temp=_find(tempF, function(o) { return o.getId() ==first_fid; });
          // 如果不在被移动的元素之列，则需要删除
          if(!temp){
            feat.set("node1",null); // 管道删除节点
          }
        }
        let node2=feat.get("node2")||"";
        let last_point=this.source_.getFeatureById(node2);
        if(last_point){
          let last_fid=last_point.getId();
          let temp=_find(tempF, function(o) { return o.getId() ==last_fid; });
          // 如果不在被移动的元素之列，则需要删除
          if(!temp){
            feat.set("node2",null); // 管道删除节点
          }
        }

        // 关联的设备节点，也应该同步移动
        let devices=feat.get("devices");
        if(devices){
          devices.forEach(dId=>{
            let d=this.source_.getFeatureById(dId);
            if(d){
              let tgeo=d.getGeometry();
              // 进行图形移动
              tgeo.translate(deltaX, deltaY); 
            }
          });
        }

        // 进行图形移动
        geometry.translate(deltaX, deltaY); 

      } else { // 如果是节点，设备节点不可以被单独移动
        // 根据node的类型，决定是否绑定多个线段
        let nodeProps=feat.getProperties(); // 获取吸附节点的属性信息
        if(nodeProps._node_type!=2){
          // 进行图形移动
          geometry.translate(deltaX, deltaY);
          // 对关联的线段进行处理
          let lines=feat.get("lines");
          if(lines&&lines.length>0){
            // 返回在选中范围内的线段
            lines=lines.filter(fid=>{
              let temp=_find(tempF, function(o) { return o.getId() ==fid; });
              // 如果不在被移动的元素之列，则需要删除
              return !!temp;
            });
            // 重新设置管道
            feat.set("lines",lines);
          }
        }
      }
    });
  }else{
    // 没有选中的要素不进行移动
    if(this.feature_){
      let map=evt.map;
      // 如果移动的是线段的端点
      if(this.geometry_type_=="LineString_Point"){
        // 移动的是直线，且是端点，需要根据端点的坐标，延长线段
        let feature=this.feature_;
        let geom=feature.getGeometry();
        // 针对线段单独操作
        // 如果是第一个点，则延长线段
        if(this.isFirst){
          this.first_coor=evt.coordinate;
          geom.setCoordinates([this.first_coor,this.last_coor]);

          // 如果有相应的绑定的线段，需要进行同步删除
          let node1=feature.get("node1")||"";
          let first_point=this.source_.getFeatureById(node1);
          if(first_point){
            let lines=first_point.get("lines");
            if(lines){
              let fid=this.feature_.getId();
              lines=lines.filter(lId=>{
                return lId!=fid;
              })
            }
            first_point.set("lines",lines);
          }
          feature.set("node1",null);
        }
        // 如果是最后一个点，则延长线段
        if(this.isLast){
          this.last_coor=evt.coordinate;
          geom.setCoordinates([this.first_coor,this.last_coor]);
          
          let node2=feature.get("node2")||"";
          let last_point=this.source_.getFeatureById(node2);
          if(last_point){
              let lines=last_point.get("lines");
              if(lines){
                let fid=this.feature_.getId();
                lines=lines.filter(lId=>{
                  return lId!=fid;
                })
              }
              last_point.set("lines",lines);
          }
          feature.set("node2",null);
        }
  
        // 如果此时线段上有设备点，则应想办法同步移动设备点
        // 因为记录了设备点的比率信息，所以可以通过设备位于线段的比率进行重新计算
        let devices=this.feature_.get("devices");
        if(devices){
          for(let dId of devices){
            let item=this.source_.getFeatureById(dId);
            let devices_geom=item.getGeometry();
            let nodeProps=item.getProperties();
            let ration=nodeProps.ration;
            if(ration){
              let first_point=geom.getFirstCoordinate();
              let last_point=geom.getLastCoordinate();
              let x=ration[0]*(last_point[0]-first_point[0])+first_point[0];
              let y=ration[1]*(last_point[1]-first_point[1])+first_point[1];
              devices_geom.setCoordinates([x,y]);
            }
          }
        }
  
      } else { // 如果移动的不是线段的端点，则进行图形的移动
  
        const deltaX = evt.coordinate[0] - this.coordinate_[0];
        const deltaY = evt.coordinate[1] - this.coordinate_[1];
        this.coordinate_[0] = evt.coordinate[0];
        this.coordinate_[1] = evt.coordinate[1];
  
        const geometry = this.feature_.getGeometry();
        // 判断移动的是线段还是节点，根据线段还是节点，将关联信息去除
        if(this.geometry_type_=="LineString"){ 
          geometry.translate(deltaX, deltaY); // 进行图形移动
  
          // 修改关连节点
          let node1=this.feature_.get("node1")||"";
          let first_point=this.source_.getFeatureById(node1);
          // 因为一个点，可能关连多条线段，所以需要遍历相关的数组，找到该线段然后移除
          if(first_point){
            // 删除管道和节点的关联
            this.feature_.set("node1",null);
            // 删除节点和管道的关联
            let lines=first_point.get("lines");
            if(lines){
              let fid=this.feature_.getId();
              lines=lines.filter(item=>{
                return item!=fid;
              })
            }
            first_point.set("lines",lines);
          }
          // 删除后一个点的关连线段数组
          let node2=this.feature_.get("node2")||"";
          let last_point=this.source_.getFeatureById(node2);
          if(last_point){
            // 删除管道和节点的关联
            this.feature_.set("node2",null);
            // 删除节点和管道的关联
            let lines=last_point.get("lines");
            if(lines){
              let fid=this.feature_.getId();
              lines=lines.filter(item=>{
                return item!=fid;
              })
            }
            last_point.set("lines",lines);
          }
  
          // 同步移动线段上面的设备节点信息
          let devices=this.feature_.get("devices");
          if(devices){
            for(let dId of devices){
              let item=this.source_.getFeatureById(dId);
              let devices_geom=item.getGeometry();
              devices_geom.translate(deltaX,deltaY);
            }
          }
        } else {
          // 判断是否有绑定的线段，如果有绑定的线段，则进行同步坐标更改
          // 获取移动元素的属性
          let nodeProps=this.feature_.getProperties();
          // 只有类型为0，或者1时需要移动关联线段，否在只能移动自身
          if(nodeProps._node_type==0||nodeProps._node_type==1){
            // 进行图形移动
            geometry.translate(deltaX, deltaY);
  
            // 获取节点的坐标
            let geo_point=geometry.getCoordinates();
  
            // 修改关联线段
            let lines=this.feature_.get("lines");
            // 如果节点存在关连的相关的线段，则进行线段的修改
            if(lines){
              lines.forEach(lID=>{
                lID=lID||"";
                let line=this.source_.getFeatureById(lID);
                if(!line){
                  return;
                }
                // 判断节点类型
                let line_geom=line.getGeometry();
                let line_first=line_geom.getFirstCoordinate();
                let line_last=line_geom.getLastCoordinate();
  
                // 判断该移动节点是否绑定到了线段的第一个节点上
                let node1=line.get("node1")||"";
                let first_point=this.source_.getFeatureById(node1);
                if(first_point&&first_point==this.feature_){
                    line_geom.setCoordinates([geo_point,line_last]);
                }
                // 判断该移动节点是否是否绑定到了线段的最后一个点上
                let node2=line.get("node2")||"";
                let last_point=this.source_.getFeatureById(node2);
                if(last_point&&last_point==this.feature_){
                    line_geom.setCoordinates([line_first,geo_point]);
                }
  
                // 重新设置管线的样式，主要就是在包含了箭头的情况下
                renderFeature(line);
  
                // 除了修改线段的端点之外，线段上的设备信息也要同步修改
                let devices=line.get("devices");
                if(devices){
                  for(let dID of devices){
                    dID=dID||"";
                    let item=this.source_.getFeatureById(dID);
                    if(!item){
                      continue;
                    }
                    let devices_geom=item.getGeometry();
                    let nodeProps=item.getProperties();
                    let ration=nodeProps.ration;
                    if(ration){
                      let first_point=line_geom.getFirstCoordinate();
                      let last_point=line_geom.getLastCoordinate();
                      let x=ration[0]*(last_point[0]-first_point[0])+first_point[0];
                      let y=ration[1]*(last_point[1]-first_point[1])+first_point[1];
                      devices_geom.setCoordinates([x,y]);
                    }
                  }
                }
  
              })
            }
            
          }else{ // 如果节点类型是2,也就是设备节点，只能是在线上移动
            // 判断鼠标点，是否与线段可以依附
            const hitTolerance=10; // 吸附半径
            const lineFeature = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
              let geometry_=feature.getGeometry();
              // 如果找到了第一条线段
              if(geometry_ instanceof LineString){
                return feature
              }
            },{
              hitTolerance:hitTolerance
            });
            // 如果鼠标点和直线在吸附半径内
            if(lineFeature){
              let p=evt.coordinate;
              let lineGeom=lineFeature.getGeometry();
              let first=lineGeom.getFirstCoordinate();
              let last=lineGeom.getLastCoordinate();
              let r=pointToFootDist(p, first, last);
              // 设置最新坐标
              geometry.setCoordinates(r.point);
  
              // 计算比例，方便线段长度改变时重新设置设备的位置，
              // 使用减号容易出现负值，改成了加号
              let xRation=(r.point[0]-first[0])/(last[0]-first[0]);
              let yRation=(r.point[1]-first[1])/(last[1]-first[1]);
              nodeProps.ration=[xRation,yRation];
            }
          }
  
          // 重新更新属性信息
          this.feature_.setProperties(nodeProps);
  
        }
      }

      
    }
  }
  
}

/**
 * @param {import("../src/ol/MapBrowserEvent.js").default} evt Event.
 * 这里没有做什么实际上的内容，其实只是改变了鼠标的样式
 */
function handleMoveEvent(evt) {
  if (this.cursor_) {
    const map = evt.map;
    const feature = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
      return feature;
    });

    // 设置鼠标样式
    const element = evt.map.getTargetElement();
    if (feature) {
        // 修改鼠标点样式
      if (element.style.cursor != this.cursor_) {
        this.previousCursor_ = element.style.cursor;
        element.style.cursor = this.cursor_;
      }
    } else if (this.previousCursor_ !== undefined) {
      element.style.cursor = this.previousCursor_;
      this.previousCursor_ = undefined;
    }
  }

  // 设置当前鼠标的位置
  store.commit("simulation/setMouseCoor",evt.coordinate);
  
}

/**
 * @return {boolean} `false` to stop the drag sequence.
 */
function handleUpEvent(evt) {
  // 进行整体移动
  // 判断是否是整体移动, 如果有多个要素，说明是整体移动
  let tempF=store.state.simulation.curFeatures;
  let movingFeatures=[];
  if(tempF.length>1){
    movingFeatures=tempF;
  }else{
    // 当选中元素存在，且只满足一个要素时进行处理
    if(this.feature_){
      movingFeatures.push(this.feature_);
    }
  }
  // 遍历数组，进行吸附
  movingFeatures.forEach(targetFeature=>{
     // 判断是否吸附了
     const geometry = targetFeature.getGeometry();
     const map=evt.map;
     const hitTolerance=20; // 吸附半径,放大到20,因为图标可能很大

     // 如果移动的节点不是直线，需要判断节点和管线是否可以自动吸附
     if(!(geometry instanceof LineString)){
       let nodeProps=targetFeature.getProperties();
       let coor=null;
       map.forEachFeatureAtPixel(evt.pixel, function (feature) {
         let selectFlag=false;
         // 判断吸附的元素，是否就在选中元素之中,在选中的元素之中，就不要进行吸附判断了
         if(tempF.length>1){
          let fid=feature.getId();
          let te=_find(tempF, function(o) { return o.getId() ===fid; });
          if(te){
            selectFlag=true;
          }
         }
         // 进行吸附判断的时候，有可能查找到的第一个feature就是自身
         if(!selectFlag&&feature!=targetFeature){ 
           let xpixel=evt.pixel[0];
           let ypixel=evt.pixel[1];
           // 获取待吸附 feature 的类型
           let geometry_=feature.getGeometry();
           if(geometry_ instanceof LineString){
              let associateFlag=false; // 管道和节点是否需要关联
              // 判断是否靠近第一个点
              let first_coor=geometry_.getFirstCoordinate();
              let first_pixel=map.getPixelFromCoordinate(first_coor);
              if(Math.abs(xpixel-first_pixel[0])<=hitTolerance&&Math.abs(ypixel-first_pixel[1])<=hitTolerance){
                coor=first_coor;
                /**
                 * 如果已经绑定了一个点，就不应该继续绑定
                 */
                let node1=feature.get("node1");
                if(!node1){
                  // 线段绑定点
                  feature.set("node1",targetFeature.getId());
                  associateFlag=true;
                }
              }
              // 判断是否靠近最后一个点
              let last_coor=geometry_.getLastCoordinate();
              let last_pixel=map.getPixelFromCoordinate(last_coor);
              if(Math.abs(xpixel-last_pixel[0])<=hitTolerance&&Math.abs(ypixel-last_pixel[1])<=hitTolerance){
                  coor=last_coor;

                  /**
                   * 如果已经绑定了一个点，就不应该继续绑定
                   */
                  let node2=feature.get("node2");
                  if(!node2){
                      feature.set("node2",targetFeature.getId());
                      associateFlag=true;
                  }
              }

              /**
               * 如果管道和节点需要进行关联
               */
              if(associateFlag){
                 // 点绑定线段
                let lines=targetFeature.get("lines");
                if(!lines){
                    lines=[];
                }
                let fid=feature.getId();
                if(lines.indexOf(fid)<0){
                  lines.push(fid);
                }
                // 点绑定线
                targetFeature.set("lines",lines);
              }
           }
           // 修改节点的坐标，直接吸附到目标上去
           if(coor){
             geometry.setCoordinates(coor);
           }

           // 根据node的类型，决定是否绑定多个线段
           if(nodeProps._node_type!=0){
               return feature; // 返回第一找到的 feature,结束绑定操作
           }
         }
       },{
         hitTolerance:hitTolerance
       });
     }else{ // 移动了线段本身，或者是移动了线段的端点，就要进行线段端点的吸附判断

       /** 线段头节点的吸附 */
       lineSnapToPoint(targetFeature,"first_point",map); // 开头节点的绑定
       lineSnapToPoint(targetFeature,"last_point",map); // 结尾点的绑定
     }

  });
  // 触发事件监听
  this.dispatchEvent({type:"dragend",event:evt,feature:movingFeatures});

  // 重新初始化拖动
  this.initDrag_();

  return false;
}
/**
 * 重新初始化拖动
 */
function initDrag(){
  // 清空
  this.coordinate_ = null;
  this.feature_ = null;
  // 线的首位坐标
  this.geometry_type_=null;    
  this.first_coor=null;
  this.isFirst=false;
  this.last_coor=null;
  this.isLast=false;
}
/**
 * 线段依附节点
 * @param {*} coor_ 
 * @param {*} flag 
 */
function lineSnapToPoint(targetFeature,flag,map){
  const hitTolerance=10; // 吸附半径

  const geometry = targetFeature.getGeometry();
  let t_first_coor=geometry.getFirstCoordinate();
  let t_last_coor=geometry.getLastCoordinate();
  let _pixel=[];
  if(flag=="first_point"){
    _pixel=map.getPixelFromCoordinate(t_first_coor);
  }else{
    _pixel=map.getPixelFromCoordinate(t_last_coor);
  }
  
  let coor_=null; // 最后吸附的位置
  map.forEachFeatureAtPixel(_pixel, function (feature) {
    if(feature!=targetFeature){ // 进行吸附判断的时候，有可能查找到的第一个feature就是自身
      let xpixel=_pixel[0];
      let ypixel=_pixel[1];
      // 获取待吸附 feature 的类型
      let geometry_=feature.getGeometry();
      if(geometry_ instanceof LineString){ // 如果待吸附的是一条线段
          // 判断是否靠近第一个点
          let first_coor=geometry_.getFirstCoordinate();
          let first_pixel=map.getPixelFromCoordinate(first_coor);
          if(Math.abs(xpixel-first_pixel[0])<=hitTolerance&&Math.abs(ypixel-first_pixel[1])<=hitTolerance){
              coor_=first_coor;
          }
          // 判断是否靠近最后一个点
          let last_coor=geometry_.getLastCoordinate();
          let last_pixel=map.getPixelFromCoordinate(last_coor);
          if(Math.abs(xpixel-last_pixel[0])<=hitTolerance&&Math.abs(ypixel-last_pixel[1])<=hitTolerance){
              coor_=last_coor;
          }
      }else{ // 如果待吸附的不是一条直线
        // 根据node的类型，决定是否绑定多个线段
        let nodeProps=feature.getProperties(); // 获取吸附节点的属性信息
        if(nodeProps._node_type==0){ // 如果节点类型为0，则可以绑定多条线段
            let lines=feature.get("lines"); // 获取该节点是否有关联的线段
            if(!lines){
              lines=[];
            }
            let fid=targetFeature.getId();
            if(lines.indexOf(fid)<0){
              lines.push(fid);
            }
            feature.set("lines",lines); // 进行节点和线段的绑定

        }else if(nodeProps._node_type==1){ // 否则只能绑定一条线段
          let lines=feature.get("lines"); // 获取该节点是否有关联的线段
            if(!lines){
              lines=[];
            }
            // 因为节点类型1的时候，只能绑定一条线，所以需要判断
            if(lines.length<=0){
              lines.push(targetFeature.getId());
            }
            feature.set("lines",lines); // 进行节点和线段的绑定
        } // 设备节点不可以再次绑定

        // 获取吸附点坐标
        coor_=geometry_.getCoordinates();
        // 将本条线段和相关的节点进行绑定
        if(flag=="first_point"){
          targetFeature.set("node1",feature.getId());
        }else{
          targetFeature.set("node2",feature.getId());
        }

      }
    }
  },{
    hitTolerance:hitTolerance
  });
  // 如果有吸附的点
  if(coor_){
    // 重新将端点定位到吸附点上去
    if(flag=="first_point"){
      geometry.setCoordinates([coor_,t_last_coor]);
    }else{
      geometry.setCoordinates([t_first_coor,coor_]);
    }
  }


}
